import Icon, { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Popover, Spacing, Typography } from "@p0security/ui";
import classNames from "classnames";
import { MouseEventHandler, PropsWithChildren, ReactNode } from "react";
import { colors } from "styles/colors";
import { stopPropagation } from "utils/javascript";

/** A rule's name, truncated to two lines.
 *
 * The styled-components original also declared a single-column grid, but its
 * own `display: -webkit-box` (which the line clamp needs) overrode that, so the
 * grid and its gaps never took effect and are not carried over.
 */
export const StyledRoutingRuleNamePreview: React.FC<PropsWithChildren> = ({
  children,
}) => <div className="line-clamp-2 font-semibold">{children}</div>;

/** Two-column layout for a routing rule: its icon, then the label above the
 * filters that narrow it. */
export const StyledRoutingRulePreview: React.FC<
  PropsWithChildren<{
    className?: string;
    onClick?: MouseEventHandler<HTMLDivElement>;
  }>
> = ({ children, className, onClick }) => (
  <div
    className={classNames(
      "grid grid-cols-[min-content_1fr] items-center gap-x-2 gap-y-1",
      "[&_.icon-container]:col-start-1 [&_.icon-container]:row-start-1 [&_.icon-container]:h-[22px] [&_.icon-container]:w-5",
      "[&_.label]:col-start-2! [&_.label]:row-start-1! [&_.label]:mb-0! [&_.label]:line-clamp-2!",
      "[&_.filters]:col-start-2 [&_.filters]:row-start-2 [&_.filters]:mb-0",
      "[&_img]:col-start-1! [&_img]:row-start-1! [&_img]:w-5! [&_img]:text-base!",
      "[&_.anticon]:col-start-1! [&_.anticon]:row-start-1! [&_.anticon]:w-5! [&_.anticon]:text-base!",
      className
    )}
    onClick={onClick}
  >
    {children}
  </div>
);

export const StyledRoutingRulePreviewComponent: React.FC<{
  icon: typeof Icon;
  label: string;
  content?: ReactNode;
}> = ({ icon: Icon, label, content }) => {
  return (
    <Flex vertical align="flex-start">
      <Flex align="center" justify="center" gap="8px">
        <Icon className="w-5" />
        <Typography.Text>{label}</Typography.Text>
      </Flex>
      {content && (
        <Spacing asChild mt={20}>
          <div className="w-full">{content}</div>
        </Spacing>
      )}
    </Flex>
  );
};

export const RoutingRulePopover: React.FC<{
  icon: ReactNode;
  text: string;
  popoverContent?: ReactNode;
}> = ({ icon, text, popoverContent }) => (
  <StyledRoutingRulePreview>
    <div className="icon-container">{icon}</div>
    <Flex gap={8}>
      <span>{text}</span>
      {popoverContent && (
        <Popover
          content={
            <div style={{ maxWidth: 350 }} onClick={stopPropagation}>
              {popoverContent}
            </div>
          }
          trigger="hover"
        >
          <InfoCircleOutlined
            /* eslint-disable-line react/forbid-component-props */ style={{
              color: colors.status.info.light,
              cursor: "pointer",
            }}
            onClick={stopPropagation}
          />
        </Popover>
      )}
    </Flex>
  </StyledRoutingRulePreview>
);

/**
 * Styles for Rule Editor
 */

/** Label/value pairs describing a rule, with the labels right-aligned against
 * the values they introduce. */
export const StyledRuleMetadataEditor: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="grid grid-cols-[max-content_1fr] items-center gap-2 text-right">
    {children}
  </div>
);

/** The collapsed "n filters" summary and the popover affordance beside it.
 *
 * `text-p0-neutral-45` on the popover matches the color Ant Design uses for
 * secondary text. */
export const StyledResourceFilterCount: React.FC<
  PropsWithChildren<{
    className?: string;
    onClick?: MouseEventHandler<HTMLDivElement>;
  }>
> = ({ children, className, onClick }) => (
  <div
    className={classNames(
      "grid grid-cols-[max-content_1fr] items-center gap-x-2",
      "[&_.text]:col-start-1! [&_.text]:row-start-1! [&_.text]:mb-0!",
      "[&_.popover]:col-start-2! [&_.popover]:row-start-1! [&_.popover]:text-p0-neutral-45!",
      className
    )}
    onClick={onClick}
  >
    {children}
  </div>
);

/** The expanded filter editor: a fixed-width key column, the pattern controls,
 * and a trailing cancel affordance. */
export const StyledResourceFilterEdit: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="grid max-w-[800px] grid-cols-[150px_1fr_min-content] items-center gap-2 [&_.filter-key-and-pattern]:col-start-2 [&_.filter-key-and-pattern]:flex [&_.filter-key-and-pattern]:flex-col [&_.filter-key-and-pattern]:gap-2 [&_.filter-cancel]:col-start-3 [&_.filter-cancel]:row-start-1 [&_.filter-cancel]:justify-self-end [&_.filter-part]:grid [&_.filter-part]:grid-cols-[150px_1fr] [&_.filter-part]:items-center [&_.filter-part]:gap-x-2">
    {children}
  </div>
);
